/**
 * Per-user memory for TrainingWheelsHint: how many times a hint has been
 * shown to this user, and whether they have since done the thing it was
 * teaching ("done").
 *
 * A hint is for someone new to a screen. It must stop on its own, so the rule
 * is: show until the user has seen it `showCap` times, or has done the thing,
 * whichever comes first. Both facts live in localStorage under one record per
 * (user, hint), so a different person on the same browser starts fresh.
 *
 * Storage can fail in ways that are not exotic: Safari private mode and
 * locked-down embedded webviews throw on access, and a full quota throws on
 * write. A hint that cannot remember that it has been shown would show on
 * every visit forever, which is worse than never showing it. So every storage
 * call is guarded, and when the count cannot be kept the fallback is an
 * in-memory set that caps the hint at ONE showing per page load.
 *
 * The same fallback covers a session with no readable user id, because a
 * record that cannot be attributed to a user cannot be kept per user.
 */

import { readSessionClaims } from '../../../utils/session';

const KEY_PREFIX = 'avrio.hint.v1';

interface HintRecord {
  shown: number;
  done: boolean;
}

type ReadResult =
  { persisted: true; record: HintRecord } | { persisted: false };

/** Hints already shown this page load whose count could not be persisted. */
const shownWithoutPersistence = new Set<string>();

const FRESH_RECORD: HintRecord = { shown: 0, done: false };

const storageKeyFor = (hintKey: string): string | null => {
  const claims = readSessionClaims<{ userId?: number }>();
  if (!claims?.userId) return null;
  return `${KEY_PREFIX}:${claims.userId}:${hintKey}`;
};

const parseRecord = (raw: string): HintRecord => {
  try {
    const value = JSON.parse(raw) as Partial<HintRecord> | null;
    if (
      value &&
      typeof value.shown === 'number' &&
      Number.isFinite(value.shown) &&
      value.shown >= 0 &&
      typeof value.done === 'boolean'
    ) {
      return { shown: Math.floor(value.shown), done: value.done };
    }
  } catch {
    /* fall through: unreadable is treated as never shown */
  }
  // A malformed record is treated as a fresh one and overwritten on the next
  // write. Treating it as "exhausted" would silently retire the hint for that
  // user on the strength of corrupt data.
  return FRESH_RECORD;
};

const readRecord = (hintKey: string): ReadResult => {
  const key = storageKeyFor(hintKey);
  if (!key) return { persisted: false };
  try {
    const raw = localStorage.getItem(key);
    return {
      persisted: true,
      record: raw === null ? FRESH_RECORD : parseRecord(raw),
    };
  } catch {
    return { persisted: false };
  }
};

const writeRecord = (hintKey: string, record: HintRecord): boolean => {
  const key = storageKeyFor(hintKey);
  if (!key) return false;
  try {
    localStorage.setItem(key, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
};

/** Whether this user should be shown the hint now. */
export const shouldShowHint = (hintKey: string, showCap: number): boolean => {
  // First, whatever storage says: a showing that could not be written down is
  // only remembered here, and storage that reads fine but refuses writes would
  // otherwise report "never shown" on every visit.
  if (shownWithoutPersistence.has(hintKey)) return false;
  const result = readRecord(hintKey);
  if (!result.persisted) return true;
  return !result.record.done && result.record.shown < showCap;
};

/** Counts one showing. Call when the hint actually appears. */
export const recordHintShown = (hintKey: string): void => {
  const result = readRecord(hintKey);
  if (!result.persisted) {
    shownWithoutPersistence.add(hintKey);
    return;
  }
  const written = writeRecord(hintKey, {
    ...result.record,
    shown: result.record.shown + 1,
  });
  if (!written) shownWithoutPersistence.add(hintKey);
};

/**
 * The user has done what the hint was teaching, so it never needs to show
 * again. Safe to call from anywhere, whether or not the hint is mounted.
 */
export const markHintDone = (hintKey: string): void => {
  const result = readRecord(hintKey);
  if (!result.persisted) {
    shownWithoutPersistence.add(hintKey);
    return;
  }
  const written = writeRecord(hintKey, { ...result.record, done: true });
  if (!written) shownWithoutPersistence.add(hintKey);
};
